import { WorkOutlineOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { JobOfferData } from '../ClientProfile';

import { OCCUPATION_CODE_KEYS } from './profileFieldUtils';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { RootState } from 'redux/store';
import { rowCountryEnabled, showHistoricalField } from 'utils/countries';

type JobOfferProps = {
    jobOfferData: JobOfferData[] | null;
    mobile: boolean;
};

const JobOffer = ({ jobOfferData, mobile }: JobOfferProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes } = useEnabledCountries();

    return (
        <>
            {jobOfferData && jobOfferData.length > 0 ? (
                <>
                    {jobOfferData.map((offer: JobOfferData, index: number) => {
                        const isCanada = rowCountryEnabled(codes, offer.country, 'CA');
                        const showNomination = showHistoricalField(
                            offer.country,
                            ['CA', 'AU', 'UK'],
                            offer.nomination
                        );
                        const showPostcode = showHistoricalField(offer.country, ['AU'], offer.zip);

                        return (
                            <div key={index}>
                                <S.InfoItem>
                                    <Tooltip title={t('country', { lng: language })}>
                                        <S.Label>{t('country', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={offer.country}>
                                        <S.Info mobile={mobile}>{offer.country}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('region', { lng: language })}>
                                        <S.Label>{t('region', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={offer.region}>
                                        <S.Info mobile={mobile}>{offer.region}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                {isCanada && (
                                    <S.InfoItem>
                                        <Tooltip title={t('workPermit', { lng: language })}>
                                            <S.Label mobile={mobile}>
                                                {t('workPermit', { lng: language })}
                                            </S.Label>
                                        </Tooltip>
                                        <Tooltip title={offer.work_permit}>
                                            <S.Info mobile={mobile}>{offer.work_permit}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                {showNomination && (
                                    <S.InfoItem>
                                        <Tooltip title={t('nomination', { lng: language })}>
                                            <S.Label>{t('nomination', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={offer.nomination}>
                                            <S.Info mobile={mobile}>{offer.nomination}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('hourlyWage', { lng: language })}>
                                        <S.Label>{t('hourlyWage', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={offer.hourly_wage}>
                                        <S.Info mobile={mobile}>{offer.hourly_wage}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('profession', { lng: language })}>
                                        <S.Label>{t('profession', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={offer.profession}>
                                        <S.Info mobile={mobile}>{offer.profession}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <ProfileFields
                                    record={offer}
                                    keys={OCCUPATION_CODE_KEYS}
                                    mobile={mobile}
                                />
                                {showPostcode && (
                                    <ProfileFields record={offer} keys={['zip']} mobile={mobile} />
                                )}
                            </div>
                        );
                    })}
                </>
            ) : (
                <Result
                    icon={<WorkOutlineOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No job offer details have been added yet."
                />
            )}
        </>
    );
};

export default JobOffer;
